<script lang="ts">
  import { onMount } from "svelte";
  import { PublicClientApplication } from "@azure/msal-browser";
  import { handleSuccessfulAuth, redirectUrl } from "../services/security/auth-handler.js";
  import { logAuthAttempt, logAuthError, logAuthSuccess, logInfo, logError, logWarning } from "../services/logging/log-handler.js";
  import { Label } from "flowbite-svelte";
  import { getEnvironment } from "../config/env.js";
  import { initializeOnLoginPage } from "../services/cache/cache-manager.js";

  const config = getEnvironment();
  let problemEncountered = false;
  let isInitializing = true;

  // Handle error timeout
  $: if (problemEncountered) {
    setTimeout(() => {
      window.location.href = "/login";
    }, 10000);
  }

  /**
   * Get the MSAL configuration
   */
  function getMsalConfig() {
    return {
      auth: {
        clientId: config.clientId ?? "",
        authority: config.signInAuthority ?? "",
        knownAuthorities: config.knownAuthorities ?? [],
        redirectUri: config.redirectUri ?? "",
        navigateToLoginRequestUrl: config.navigateToLoginRequestUrl ?? false,
        validateAuthority: false,
        postLogoutRedirectUri: config.redirectUri ?? "",
      },
      cache: {
        cacheLocation: "sessionStorage" as const,
        storeAuthStateInCookie: false,
      },
    };
  }

  /**
   * Check if we're handling a B2C redirect callback.
   * B2C redirects back with state/code in the URL hash (e.g., #state=...&code=...)
   * We must NOT clear sessionStorage before MSAL processes this, or authentication will fail.
   * 
   * IMPORTANT: We also check sessionStorage for pending MSAL state because:
   * - Some redirects/refreshes can lose the URL hash
   * - MSAL stores the hash in sessionStorage (msal.*.urlHash) when it first sees it
   * - If there's pending MSAL state, we should let MSAL try to process it
   */
  function isB2CRedirectCallback(): boolean {
    const hash = window.location.hash;
    // Check for state or code in hash - these indicate a B2C redirect callback
    const hasHashState = hash.includes('state=');
    const hasHashCode = hash.includes('code=');
    const hasHashError = hash.includes('error=');
    
    // Also check sessionStorage for pending MSAL interaction state
    // This catches cases where the URL hash was lost but MSAL state still exists
    let hasPendingMsalState = false;
    let hasStoredUrlHash = false;
    
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          if (key === 'msal.interaction.status') {
            hasPendingMsalState = true;
          }
          if (key.includes('.urlHash')) {
            hasStoredUrlHash = true;
          }
        }
      }
    } catch (e) {
      logError(e, "LoginB2C", undefined, { message: "Error checking sessionStorage" });
    }
    
    const isCallbackFromHash = hasHashState || hasHashCode || hasHashError;
    const isCallbackFromStorage = hasPendingMsalState || hasStoredUrlHash;
    const isCallback = isCallbackFromHash || isCallbackFromStorage;
    
    return isCallback;
  }

  /**
   * Clear stale MSAL interaction state from sessionStorage.
   * MSAL uses 'msal.interaction.status' to track ongoing interactions.
   * If this exists from a previous (possibly incomplete) session, it can block new logins.
   * Also clears any other stale MSAL state that might interfere.
   */
  function clearStaleMsalState(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('msal.')) {
        keysToRemove.push(key);
      }
    }
    
    if (keysToRemove.length > 0) {
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
    }
  }

  onMount(async () => {
    if (!config.useB2C) {
      logError(new Error("B2C authentication is not enabled"), "LoginB2C", undefined);
      return;
    }

    // CRITICAL: Check if we're handling a B2C redirect BEFORE any cleanup or MSAL instantiation
    // If we're returning from B2C with auth response, we must NOT clear sessionStorage
    // because MSAL needs the stored state to validate the response
    const handlingRedirect = isB2CRedirectCallback();
    
    if (!handlingRedirect) {
      // Only clean up if this is a fresh login (no B2C redirect to process)
      // This ensures a fresh start for each login session
      
      // Clear stale MSAL state FIRST (before general cleanup and MSAL instantiation)
      // This ensures MSAL doesn't read stale state when it's created
      clearStaleMsalState();
      
      try {
        await initializeOnLoginPage();
      } catch (error) {
        logError(error, "LoginB2C", undefined, { message: "Cache cleanup warning" });
      }
    }
    
    isInitializing = false;

    // Create MSAL instance AFTER cleanup (for fresh logins) to ensure it doesn't read stale state
    // This is critical - if MSAL is created before cleanup, it may cache stale sessionStorage values
    logInfo("Creating MSAL instance", "LoginB2C");
    const msalInstance = new PublicClientApplication(getMsalConfig());
    await msalInstance.initialize();

    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response) {

        const urlParams = new URLSearchParams(window.location.search);
      }

      if (!response) {
        try {
          try {
            logAuthAttempt("azure-b2c", { operationName: "loginRedirect" });
          } catch (logError) {
            logError(logError, "LoginB2C", undefined, { message: "Failed to log auth attempt" });
          }
          
          await msalInstance.loginRedirect({
            scopes: ["openid", "profile"],
            prompt: "select_account",
          });
          
          // Note: This line should NOT execute because loginRedirect navigates away
          logInfo("After loginRedirect (unexpected - should have navigated)", "LoginB2C");
        } catch (error: unknown) {
          logError(error, "LoginB2C", undefined, { message: "loginRedirect threw an error" });

          // Enhanced error handling with detailed logging
          if (typeof error === "object" && error !== null) {
            // Check for specific error types
            if ("errorMessage" in error && typeof error.errorMessage === "string") {

              if (error.errorMessage.includes("AADB2C90091")) {
                // Handle user cancellation - redirect back to login
                window.location.href = "/login";
                return;
              }

              if (error.errorMessage.includes("AADB2C90077")) {
                // Handle MFA cancellation - redirect back to login
                window.location.href = "/login";
                return;
              }
            }

            // Check for error code
            if ("errorCode" in error && typeof error.errorCode === "string") {

              switch (error.errorCode) {
                case "AADB2C90118":
                  // Create a new MSAL instance with password reset authority
                  const passwordResetMsalConfig = {
                    auth: {
                      clientId: config.clientId ?? "",
                      authority: "https://openhgappdev.b2clogin.com/openhgappdev.onmicrosoft.com/B2C_1_PwdReset",
                      knownAuthorities: config.knownAuthorities ?? [],
                      redirectUri: config.redirectUri ?? "",
                      navigateToLoginRequestUrl: config.navigateToLoginRequestUrl ?? false,
                      validateAuthority: false,
                      postLogoutRedirectUri: config.redirectUri ?? "",
                    },
                    cache: {
                      cacheLocation: "sessionStorage",
                      storeAuthStateInCookie: false,
                    },
                  };

                  const passwordResetMsalInstance = new PublicClientApplication(passwordResetMsalConfig);
                  await passwordResetMsalInstance.initialize();

                  await passwordResetMsalInstance.loginRedirect({
                    scopes: ["openid", "profile"],
                    prompt: "login",
                  });
                  return;
                case "AADB2C90091":
                case "AADB2C90077":
                  window.location.href = "/login";
                  return;
                default:
                  logError(new Error(`Unhandled error code: ${error.errorCode}`), "LoginB2C", undefined);
              }
            }
          }

          logError(error, "LoginB2C", undefined, { message: "login failed" });
        }
      } else {
        // Check for error responses
        if ((response as any).errorCode || (response as any).errorMessage) {

          // Log authentication error
          logAuthError(new Error(`B2C Error: ${(response as any).errorCode} - ${(response as any).errorMessage}`), "azure-b2c", {
            operationName: "handleRedirectPromise",
            errorCode: (response as any).errorCode,
            errorMessage: (response as any).errorMessage,
            subError: (response as any).subError,
          });

          // TODO: Uncomment this when we have a password reset license
          // Handle specific error scenarios
          if ((response as any).errorCode === "AADB2C90118" || ((response as any).errorMessage && (response as any).errorMessage.includes("AADB2C90118"))) {
            logInfo("Redirecting to password reset due to 'Forgot Password'", "LoginB2C");
            window.location.href = "/password-reset";
            return;
          }

          if ((response as any).errorCode === "AADB2C90091" || (response as any).errorCode === "AADB2C90077" || ((response as any).errorMessage && ((response as any).errorMessage.includes("AADB2C90091") || (response as any).errorMessage.includes("AADB2C90077")))) {
            logInfo("Handling user cancellation - redirecting to login", "LoginB2C");
            window.location.href = "/login";
            return;
          }
          logError(new Error(`Unhandled error response: ${response}`), "LoginB2C", undefined);
          // For other errors, log and redirect to login
          window.location.href = "/login";
          return;
        }

        // Handle successful response
        const claims = response.idTokenClaims as {
          displayName?: string;
          name?: string;
          oid?: string;
          emails?: string[];
          email?: string;
        };

        const displayName = claims?.displayName || claims?.name;
        const object_id = claims?.oid;
        const username = claims?.email || claims?.emails?.[0];

        if (displayName && object_id && username) {
          const token = response.idToken || response.accessToken;
          if (!token) {
            logError(new Error("No token found in response"), "LoginB2C", undefined);
            return;
          }

          try {
            const success = await handleSuccessfulAuth(object_id, token);
            if (!success) {
              logError(new Error("Failed to handle successful auth"), "LoginB2C", undefined);
              problemEncountered = true;
              return;
            }

            let url = "/";
            const unsubscribe = redirectUrl.subscribe((value) => {
              url = value || "/";
            });
            unsubscribe();
            window.location.href = url;
          } catch (error) {
            logError(error, "LoginB2C", undefined, { message: "Error during auth handling" });
          }
        } else {
          logError(new Error("Missing required user information"), "LoginB2C", undefined, {
            hasDisplayName: !!displayName,
            hasObjectId: !!object_id,
            hasUsername: !!username,
          });
        }
      }
    } catch (error: unknown) {
      logError(error, "LoginB2C", undefined, { message: "LoginB2C - handleRedirectPromise error" });

      // Handle the specific "Forgot Password" exception
      if (error instanceof Error) {

        if (error.message.includes("AADB2C90118")) {
          // Create a new MSAL instance with password reset authority
          const passwordResetMsalConfig = {
            auth: {
              clientId: config.clientId ?? "",
              authority: config.pwdResetAuthority,
              knownAuthorities: config.knownAuthorities ?? [],
              redirectUri: config.redirectUri ?? "",
              navigateToLoginRequestUrl: config.navigateToLoginRequestUrl ?? false,
              validateAuthority: false,
              postLogoutRedirectUri: config.redirectUri ?? "",
            },
            cache: {
              cacheLocation: "sessionStorage",
              storeAuthStateInCookie: false,
            },
          };

          const passwordResetMsalInstance = new PublicClientApplication(passwordResetMsalConfig);
          await passwordResetMsalInstance.initialize();

          await passwordResetMsalInstance.loginRedirect({
            scopes: ["openid", "profile"],
            prompt: "login",
          });
          return;
        }

        if (error.message.includes("AADB2C90091") || error.message.includes("AADB2C90077")) {
          window.location.href = "/login";
          return;
        }
      }
      window.location.href = "/login";
    }
  });
</script>

<div class="login-page">
  <div class="login-container">
    {#if problemEncountered}
      <Label class="text-center">
        An error occurred while logging in.
        <br />
        <br />
        Please try again, but if the problem persists contact support.
      </Label>
    {:else}
      <div class="flex items-center justify-center">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <Label class="ml-3">One moment...</Label>
      </div>
    {/if}
  </div>
</div>
